import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import ChartSummary from '../../components/ChartSummary';
import { formatCurrency } from '../../Utils/chartUtils';
import AutoLoanChart from '../../charts/AutoLoanChart';

interface AutoLoanCalculatorPageProps {
  formData: FormData;
  selectedPage: string;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
  setSnackbarMessage: (value: React.SetStateAction<string>) => void;
  showSnackbar: boolean;
  snackbarMessage: string;
  setChartData: (value: React.SetStateAction<number[][] | null>) => void;
  setShowSnackbar: (value: React.SetStateAction<boolean>) => void;
  chartData: number[][] | null;
}

interface LoanPayment {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

const AutoLoanCalculatorPage: React.FC<AutoLoanCalculatorPageProps> = ({
  formData,
  selectedPage,
  handleInputChange,
  onValueChange,
  setSnackbarMessage,
  showSnackbar,
  snackbarMessage,
  setChartData,
  setShowSnackbar,
  chartData,
}) => {
  const [loanPrincipal, setLoanPrincipal] = useState<string>('25000');
  const [interestRate, setInterestRate] = useState<string>('5.5');
  const [loanTermMonths, setLoanTermMonths] = useState<string>('60');
  const [paymentSchedule, setPaymentSchedule] = useState<LoanPayment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleLoanInputChange = (value: string, field: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    
    switch (field) {
      case 'loanPrincipal':
        setLoanPrincipal(numericValue.toString());
        break;
      case 'interestRate':
        setInterestRate(value);
        break;
      case 'loanTermMonths':
        setLoanTermMonths(numericValue.toString());
        break;
    }
  };

  const calculateMonthlyPayment = useCallback((
    principal: number,
    annualRate: number,
    termMonths: number
  ): number => {
    if (principal <= 0 || annualRate < 0 || termMonths <= 0) {
      return 0;
    }

    if (annualRate === 0) {
      return principal / termMonths;
    }

    const monthlyRate = annualRate / 100 / 12;
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                   (Math.pow(1 + monthlyRate, termMonths) - 1);
    
    return payment;
  }, []);

  const calculateLoanAmortization = useCallback((
    principal: number,
    annualRate: number,
    monthlyPayment: number
  ): LoanPayment[] => {
    const monthlyRate = annualRate / 100 / 12;
    const payments: LoanPayment[] = [];
    let remainingBalance = principal;
    let month = 0;

    // Validate inputs
    if (principal <= 0 || annualRate < 0 || monthlyPayment <= 0) {
      return [];
    }

    // Check if monthly payment covers interest
    const firstMonthInterest = remainingBalance * monthlyRate;
    if (monthlyPayment <= firstMonthInterest) {
      throw new Error('Monthly payment is too low to cover interest. Loan will never be paid off.');
    }

    while (remainingBalance > 0.01 && month < 600) { // Cap at 50 years
      month++;
      const interestPayment = remainingBalance * monthlyRate;
      let principalPayment = monthlyPayment - interestPayment;
      
      // Handle final payment
      if (principalPayment > remainingBalance) {
        principalPayment = remainingBalance;
        const actualPayment = principalPayment + interestPayment;
        remainingBalance = 0;
        
        payments.push({
          month,
          payment: actualPayment,
          principal: principalPayment,
          interest: interestPayment,
          balance: 0
        });
        break;
      }
      
      remainingBalance -= principalPayment;
      
      payments.push({
        month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: remainingBalance
      });
    }

    return payments;
  }, []);

  const inputData = useMemo(() => ({
    loanPrincipal: parseFloat(loanPrincipal) || 0,
    interestRate: parseFloat(interestRate) || 0,
    loanTermMonths: parseFloat(loanTermMonths) || 0
  }), [loanPrincipal, interestRate, loanTermMonths]);

  const calculateLoan = useCallback(async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const { loanPrincipal: principal, interestRate: rate, loanTermMonths: termMonths } = inputData;
      
      if (principal <= 0 || rate < 0 || termMonths <= 0) {
        setSnackbarMessage('Please enter valid loan details');
        setShowSnackbar(true);
        return;
      }

      // Calculate the monthly payment based on the inputs
      const monthlyPayment = calculateMonthlyPayment(principal, rate, termMonths);
      
      if (monthlyPayment <= 0) {
        setSnackbarMessage('Unable to calculate monthly payment with these parameters');
        setShowSnackbar(true);
        return;
      }

      const schedule = calculateLoanAmortization(principal, rate, monthlyPayment);
      
      if (schedule.length === 0) {
        setSnackbarMessage('Unable to calculate loan schedule with these parameters');
        setShowSnackbar(true);
        return;
      }

      setPaymentSchedule(schedule);
      
      // Create chart data for visualization
      const chartDataArray = schedule.map((payment, index) => [
        index + 1, // Month
        payment.balance, // Remaining balance
        payment.principal, // Principal payment
        payment.interest // Interest payment
      ]);
      
      setChartData(chartDataArray);
    } catch (error) {
      console.error('Error calculating loan:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An error occurred calculating the loan');
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [inputData, calculateLoanAmortization, calculateMonthlyPayment, setChartData, setSnackbarMessage, setShowSnackbar, isLoading]);

  // Auto-calculate with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateLoan();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [inputData, calculateLoan]);


  const generateSummaryItems = (): string[] => {
    if (!paymentSchedule.length) return [];

    const totalPayments = paymentSchedule.reduce((sum, payment) => sum + payment.payment, 0);
    const totalInterest = paymentSchedule.reduce((sum, payment) => sum + payment.interest, 0);
    const loanTerm = paymentSchedule.length;
    const monthlyPayment = paymentSchedule[0]?.payment || 0;

    return [
      `Original loan amount: ${formatCurrency(parseFloat(loanPrincipal))}`,
      `Monthly payment: ${formatCurrency(monthlyPayment)}`,
      `Loan term: ${loanTerm} months (${Math.round(loanTerm / 12 * 10) / 10} years)`,
      `Total interest paid: ${formatCurrency(totalInterest)}`,
      `Total amount paid: ${formatCurrency(totalPayments)}`,
      `Interest as % of loan: ${Math.round((totalInterest / parseFloat(loanPrincipal)) * 100)}%`
    ];
  };

  return (
    <div className="auto-loan-calculator w-full px-2 sm:px-4 lg:px-6 financial-app">
      {/* Professional Header */}
      <div className="hero-section mb-6 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6">
        <div className="professional-layout">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 tracking-tight">
            {selectedPage}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Calculate your monthly auto loan payment and see your complete payment schedule
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Form Section */}
        <div className="lg:w-2/5 min-w-0">
          <Card className="w-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                <h2 className={cn("text-xl font-semibold text-card-foreground")}>Loan Details</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Enter your loan details to calculate monthly payment and schedule
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Loan Details Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Loan Information</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="loanPrincipal" className="text-sm font-medium text-foreground flex items-center gap-2">
                      Loan Principal
                      <span className="text-xs text-muted-foreground">(total amount borrowed)</span>
                    </Label>
                    <div className={cn("relative group")}>
                      <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                      <Input
                        id="loanPrincipal"
                        name="loanPrincipal"
                        type="text"
                        placeholder="25,000"
                        value={formatNumber(loanPrincipal)}
                        onChange={(e) => handleLoanInputChange(e.target.value, 'loanPrincipal')}
                        className={cn("pl-8 text-lg font-medium")}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-sm font-medium text-foreground flex items-center gap-2">
                      Annual Interest Rate
                      <span className="text-xs text-muted-foreground">(APR)</span>
                    </Label>
                    <div className={cn("relative group")}>
                      <Input
                        id="interestRate"
                        name="interestRate"
                        type="number"
                        step="0.1"
                        placeholder="5.5"
                        value={interestRate}
                        onChange={(e) => handleLoanInputChange(e.target.value, 'interestRate')}
                        className={cn("pr-8")}
                      />
                      <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loanTermMonths" className="text-sm font-medium text-foreground flex items-center gap-2">
                      Loan Term
                      <span className="text-xs text-muted-foreground">(months)</span>
                    </Label>
                    <div className={cn("relative group")}>
                      <Input
                        id="loanTermMonths"
                        name="loanTermMonths"
                        type="number"
                        step="1"
                        placeholder="60"
                        value={loanTermMonths}
                        onChange={(e) => handleLoanInputChange(e.target.value, 'loanTermMonths')}
                        className={cn("pr-16")}
                      />
                      <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>months</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Section */}
        <div className="lg:w-3/5 min-w-0" ref={chartRef}>
          <Card className="w-full h-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                  <div>
                    <h2 className="text-xl font-semibold text-card-foreground">Loan Paydown Timeline</h2>
                    <p className="text-sm text-muted-foreground">
                      Track your remaining balance over time
                    </p>
                  </div>
                </div>
                {paymentSchedule.length > 0 && !isLoading && (
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Monthly Payment</div>
                    <div className="text-2xl font-bold text-primary">
                      {formatCurrency(paymentSchedule[0]?.payment || 0)}
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="h-full">
              {paymentSchedule.length > 0 && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow">
                    <AutoLoanChart paymentSchedule={paymentSchedule} />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-4">
                    {isLoading && (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-accent/10"></div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-lg font-medium text-foreground mb-1">
                        {isLoading ? 'Calculating loan schedule...' : 'Ready to calculate'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isLoading ? 'Processing amortization schedule' : 'Enter your loan details to see results'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Summary Section */}
      {paymentSchedule.length > 0 && !isLoading && (
        <div className="mt-6">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-success to-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground">Loan Summary</h2>
                  <p className="text-sm text-muted-foreground">
                    Complete breakdown of your auto loan
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartSummary 
                summaryItems={generateSummaryItems()}
                title=""
              />
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Enhanced Error Display */}
      {showSnackbar && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg border border-destructive/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive-foreground rounded-full"></div>
              <div className="font-medium">Calculation Error</div>
            </div>
            <div className="mt-1 text-sm opacity-90">
              {snackbarMessage}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoLoanCalculatorPage;